import { Navigate, Outlet, useLocation } from "react-router-dom";

import { DevtoolsPanel } from "#app/devtools/DevtoolsPanel";
import { AccessTokenSingleton } from "#modules/auth/domain/AccessTokenSingleton";
import { useCompany } from "#modules/company/view/useCompany";
import { useUser } from "#modules/users/view/useUser";
import { useRealtimeEvents } from "#shared/hooks/useRealtimeEvents";
import { ROUTES } from "#shared/routes";
import { AppShell } from "#shared/view/components/AppShell";
import { Loader } from "#shared/view/components/Loader";

type GuardResult =
  | { type: "loading" }
  | { type: "redirect"; to: string; replace?: boolean }
  | { type: "ok" };

export const useProtectedRouteGuard = (
  pathname: string,
  options?: { isOnboardingRoute?: boolean },
): GuardResult => {
  const { user, isLoading: isUserLoading } = useUser();
  const { company, isLoading: isCompanyLoading } = useCompany();

  if (!AccessTokenSingleton.getInstance().getAccessToken()) {
    return { type: "redirect", to: `${ROUTES.login}?from=${pathname}` };
  }

  if (isUserLoading) {
    return { type: "loading" };
  }

  if (!user) {
    return { type: "redirect", to: `${ROUTES.login}?from=${pathname}` };
  }

  if (!user.companyId && pathname !== ROUTES.partnerSetCompany) {
    return { type: "redirect", to: ROUTES.partnerSetCompany };
  }

  if (!user.companyId) {
    return { type: "ok" };
  }

  if (isCompanyLoading) {
    return { type: "loading" };
  }

  const hasLeftOnboarding = Boolean(company?.onboardingExitedAt);
  const isOnboardingRoute = !!options?.isOnboardingRoute;

  if (isOnboardingRoute && hasLeftOnboarding) {
    return {
      type: "redirect",
      to: ROUTES.partner,
      replace: true,
    };
  }

  if (!isOnboardingRoute && !hasLeftOnboarding) {
    return {
      type: "redirect",
      to: ROUTES.v2Onboarding,
      replace: true,
    };
  }

  return { type: "ok" };
};

const RealtimeEventsConnection = () => {
  useRealtimeEvents();

  return null;
};

export const ProtectedAppShell = ({ noHeader = false }: { noHeader?: boolean }) => {
  const { pathname } = useLocation();
  const guard = useProtectedRouteGuard(pathname);

  if (guard.type === "loading") {
    return <Loader />;
  }

  if (guard.type === "redirect") {
    return <Navigate to={guard.to} replace={guard.replace} />;
  }

  return (
    <>
      <RealtimeEventsConnection />
      <AppShell noHeader={noHeader} />
    </>
  );
};

export const ProtectedV2Outlet = () => {
  const { pathname } = useLocation();
  const guard = useProtectedRouteGuard(pathname);

  if (guard.type === "loading") {
    return <Loader />;
  }

  if (guard.type === "redirect") {
    return <Navigate to={guard.to} replace={guard.replace} />;
  }

  return (
    <>
      <RealtimeEventsConnection />
      <Outlet />
      <DevtoolsPanel />
    </>
  );
};

export const ProtectedOnboardingV2Outlet = () => {
  const { pathname } = useLocation();
  const guard = useProtectedRouteGuard(pathname, { isOnboardingRoute: true });

  if (guard.type === "loading") {
    return <Loader />;
  }

  if (guard.type === "redirect") {
    return <Navigate to={guard.to} replace={guard.replace} />;
  }

  return <Outlet />;
};
